import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorList, type ListOfErrors } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { ADD_EDITABLE_PARENT, UPDATE_STATE } from '#app/constants/forms.ts'
import { checkIfCurrentStepHasChildren } from '#app/features/form/wizard/utils/checkIfCurrentStepHasChildren.ts'
import { type FieldComponentProps } from '#types/form/wizard.ts'

export function TextFieldComponent({
	fieldName,
	label,
	type = 'text',
	register,
	getValues,
	dispatch,
	canBeParent,
	editableParents,
	isParentProps,
}: FieldComponentProps) {
	const { t } = useTranslation('form')
	const defaultFormValue = getValues!(fieldName)
	const [showOverlay, setShowOverlay] = useState(false)
	const { onChange, ...rest } = register!(fieldName)

	useEffect(() => {
		if (canBeParent) {
			const isCurrentTreeAParent = checkIfCurrentStepHasChildren({
				id: isParentProps!.id,
				questionTree: isParentProps!.questionTree,
			})?.hasChildren

			if (isCurrentTreeAParent) {
				if (!editableParents!.includes(isParentProps!.id)) {
					setShowOverlay(true)
				}
			}
		}
	}, [canBeParent, editableParents, isParentProps])

	const setOverLayHandler = () => {
		setShowOverlay(false)
		dispatch!({
			type: ADD_EDITABLE_PARENT,
			editableParent: isParentProps!.id,
		})
	}

	const Overlay = () => (
		<div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center p-10 ">
			<div className="text-center text-primary-400">
				<p>{t('textField.changeAlert')}</p>
				<Button className="mt-4 " onClick={() => setOverLayHandler()}>
					{t('textField.yesLetMeEdit')}
				</Button>
			</div>
		</div>
	)

	return (
		<div className="relative  h-full w-full">
			{showOverlay && <Overlay />}
			{type === 'textArea' && (
				<TextareaField
					className="flex h-full w-full flex-col md:min-h-[40vh]"
					key={fieldName}
					labelProps={{
						htmlFor: fieldName,
						children: label,
						className: 'my-6 ',
					}}
					textareaProps={{
						autoFocus: showOverlay && true,
						onFocus: e => {
							const val = e.target.value
							e.target.value = ''
							e.target.value = val
						},
						className: `flex grow text-[1rem] w-full ${
							showOverlay ? 'text-gray-200' : ''
						}`,
						onChange: e => {
							onChange(e)
							dispatch!({
								type: UPDATE_STATE,
								fieldName,
								value: e.target.value,
							})
						},
						...rest,
					}}
					errors={[]}
				/>
			)}

			{type !== 'textArea' && (
				<TextField
					className="flex  flex-col "
					key={fieldName}
					labelProps={{
						htmlFor: fieldName,
						children: label,
						className: 'my-6',
					}}
					inputProps={{
						type,
						max: type === 'number' ? 95 : undefined,
						min: type === 'number' ? 18 : undefined,
						disabled: showOverlay,
						autoFocus: true,
						onFocus: e => {
							const val = e.target.value
							e.target.value = ''
							e.target.value = val
						},
						defaultValue: defaultFormValue,
						className: 'flex grow text-[1rem]',
						onChange: e => {
							onChange(e)
							dispatch!({
								type: UPDATE_STATE,
								fieldName,
								value: e.target.value,
							})
						},
						...rest,
					}}
					errors={[]}
				/>
			)}
		</div>
	)
}

export function TextField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				className="mt-2"
				{...inputProps}
			/>
			<div className=" px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				defaultValue={textareaProps.defaultValue}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				className="mt-2 text-[1rem]"
				{...textareaProps}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
